import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'iamerica.dashboardTheme';
const LEGACY_ROOT_CLASSES = [
  'theme-blue', 'theme-pink', 'theme-dark', 'theme-white', 'theme-wine',
  'theme-student-original', 'theme-student-ocean', 'theme-student-sunset',
  'theme-student-forest', 'theme-student-purple', 'theme-student-pink',
];

const normalize = (value: unknown): Theme => {
  return value === 'dark' ? 'dark' : 'light';
};

const readInitial = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* ignore */
  }
  return 'light';
};

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(readInitial);
  const [isLoading, setIsLoading] = useState(true);

  // One-time cleanup: strip any legacy multi-color theme class from <html>
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.remove(...LEGACY_ROOT_CLASSES);
  }, []);

  useEffect(() => {
    const loadTheme = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', user.id)
          .maybeSingle();
        if (error) console.error('Error loading theme:', error);
        if (data?.theme_preference) {
          const next = normalize(data.theme_preference);
          setThemeState(next);
          try { window.localStorage.setItem(STORAGE_KEY, next); } catch {/* ignore */}
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTheme();
  }, [user]);

  const setTheme = async (newTheme: Theme) => {
    const next = normalize(newTheme);
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch {/* ignore */}

    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ theme_preference: next })
          .eq('id', user.id);
        if (error) console.error('Error saving theme:', error);
      } catch (error) {
        console.error('Error saving theme:', error);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
